import { isPsaPegboardFixtureType } from "@CommonTypes/psa/FixtureRecord";
import type { ProjectRecord } from "@CommonTypes/psa/ProjectRecord";
import { PALLET_UNDER_BAY_SHARE } from "../pog/pallet";
import { canMergeLeft, canMergeRight, type FixtureCanCombine } from "../pog/canCombine";

// A PSA has no field that declares an aisle split, so a gap or an unmerged seam is only
// evidence, offered to the user as a suggestion; a split is declared on the store's fixtures.

/** In inches. Below it a gap is drawing slop between bays meant to touch. */
export const SPLIT_GAP_MIN = 1;

/** In centimetres, the unit the export declares; must stay above SPLIT_GAP_MIN to re-import as a suggestion. */
export const SPLIT_GAP_EXPORT = 30;

const CENTIMETRES_PER_INCH = 2.54;

// The Project record's `measurement`, after its name: 1 is metric (cm), 0 imperial (in).
const PROJECT_MEASUREMENT_FIELD = 11;

export const isMetricPsaProject = (project: Pick<ProjectRecord, "fields"> | undefined) =>
  Number(project?.fields[PROJECT_MEASUREMENT_FIELD]) === 1;

/** A length given in inches or centimetres, in the PSA's own unit, to the hundredth a PSA carries. */
const inPsaUnit = (length: number, unit: "in" | "cm", metric: boolean) => {
  const centimetres = unit === "cm" ? length : length * CENTIMETRES_PER_INCH;
  return Math.round((metric ? centimetres : centimetres / CENTIMETRES_PER_INCH) * 100) / 100;
};

/** SPLIT_GAP_MIN in the PSA's own unit. */
export const splitGapMinFor = (metric: boolean) => inPsaUnit(SPLIT_GAP_MIN, "in", metric);

/** SPLIT_GAP_EXPORT in the PSA's own unit: 11.81 in an imperial file. */
export const splitGapExportFor = (metric: boolean) => inPsaUnit(SPLIT_GAP_EXPORT, "cm", metric);

/** One bay's box along the run, in the PSA's own unit. */
export interface AisleSectionBounds {
  x: number;
  width: number;
}

export interface AisleSplitGapOptions {
  /** In the PSA's own unit. */
  minGap?: number;
  /** What stands on the floor between bays, a pallet in the run: a gap it stands in is no gap. */
  floorSpans?: readonly AisleSectionBounds[];
}

/** Aligned with `bounds` (walk order); null where there is no gap worth asking about. */
export const gapAfterBySectionBounds = (
  bounds: readonly AisleSectionBounds[],
  { minGap = SPLIT_GAP_MIN, floorSpans = [] }: AisleSplitGapOptions = {},
): (number | null)[] =>
  bounds.map((thisBay, index) => {
    const nextBay = bounds[index + 1];
    if (
      nextBay === undefined ||
      !Number.isFinite(thisBay.x) ||
      !Number.isFinite(thisBay.width) ||
      !Number.isFinite(nextBay.x)
    ) {
      return null;
    }

    const gapLeft = thisBay.x + thisBay.width;
    const gap = nextBay.x - gapLeft;
    const somethingStandsInTheGap = floorSpans.some(
      (span) => span.x < nextBay.x && span.x + span.width > gapLeft,
    );
    return gap > minGap && !somethingStandsInTheGap ? gap : null;
  });

export interface AisleSeamShelf {
  canCombine: FixtureCanCombine | number | null | undefined;
  /** Pegboards hang on the back panel and never merge, so they say nothing. */
  isPegboard?: boolean;
}

export interface AisleSeamSection {
  shelves: readonly AisleSeamShelf[];
}

/** Aligned with `sections` (walk order): the seams every shelf refuses to merge across. */
export const unmergedSeamAfterBySections = (sections: readonly AisleSeamSection[]): boolean[] => {
  const mergingShelvesOf = (section: AisleSeamSection | undefined) =>
    section?.shelves.filter((shelf) => !shelf.isPegboard) ?? [];

  const seams = sections.map((_section, index) => {
    const shelvesLeftOfSeam = mergingShelvesOf(sections[index]);
    const shelvesRightOfSeam = mergingShelvesOf(sections[index + 1]);
    const isSeam = index < sections.length - 1;

    return {
      merges:
        isSeam &&
        shelvesLeftOfSeam.some((shelf) => canMergeRight(shelf.canCombine)) &&
        shelvesRightOfSeam.some((shelf) => canMergeLeft(shelf.canCombine)),
      unmerged:
        isSeam &&
        shelvesLeftOfSeam.length > 0 &&
        shelvesRightOfSeam.length > 0 &&
        shelvesLeftOfSeam.every((shelf) => !canMergeRight(shelf.canCombine)) &&
        shelvesRightOfSeam.every((shelf) => !canMergeLeft(shelf.canCombine)) &&
        // NONE is the ordinary default, so a refusal counts only beside shelves that merge elsewhere.
        shelvesLeftOfSeam.some((shelf) => canMergeLeft(shelf.canCombine)) &&
        shelvesRightOfSeam.some((shelf) => canMergeRight(shelf.canCombine)),
    };
  });

  const runOffersAMerge = seams.some((seam) => seam.merges);

  return seams.map((seam) => runOffersAMerge && seam.unmerged);
};

/** A gap wins over an unmerged seam at the same seam; `gap` is in the PSA's own unit. */
export type AisleSplitEvidence = { evidence: "gap"; gap: number } | { evidence: "unmergedSeam" };

/** `bayIndex` is the 0-based index of the bay the seam follows. */
export type AisleSplitSuggestion = { bayIndex: number } & AisleSplitEvidence;

export const suggestAisleSplits = (
  bounds: readonly AisleSectionBounds[],
  seams: readonly AisleSeamSection[],
  gapOptions: AisleSplitGapOptions = {},
): AisleSplitSuggestion[] => {
  const gapAfter = gapAfterBySectionBounds(bounds, gapOptions);
  const unmergedAfter = unmergedSeamAfterBySections(seams);

  return gapAfter.flatMap((gap, bayIndex): AisleSplitSuggestion[] => {
    if (gap !== null) {
      return [{ bayIndex, evidence: "gap", gap }];
    }
    return unmergedAfter[bayIndex] ? [{ bayIndex, evidence: "unmergedSeam" }] : [];
  });
};

export interface PsaSectionFixtureLike {
  section_num: number;
  section_width: number;
  section_x?: number;
  can_combine?: number | null;
  type?: number | null;
}

/** `afterBayNo` counts bays in walk order, including floor pallets before the section. */
export type PsaAisleSplitSuggestion = {
  afterSectionNum: number;
  afterBayNo: number;
} & (
  | {
      evidence: "gap";
      gap: number;
      /** The PSA's own unit, which `gap` is in. */
      gapUnit: "in" | "cm";
    }
  | { evidence: "unmergedSeam" }
);

export interface PsaAisleSplitOptions {
  /** From the Project record (`isMetricPsaProject`); without it the file is read as inches. */
  metric?: boolean;
  /** The file's floor pallets, in the same raw space as `section_x`. */
  floorSpans?: readonly AisleSectionBounds[];
}

const UNPOSITIONED: AisleSectionBounds = { x: Number.NaN, width: Number.NaN };

/** Shared by the pog reader and the backend's fixture tables; takes raw PSA space, before any scaling. */
export const suggestAisleSplitsFromPsaFixtures = (
  fixtures: readonly PsaSectionFixtureLike[],
  { metric = false, floorSpans = [] }: PsaAisleSplitOptions = {},
): PsaAisleSplitSuggestion[] => {
  const boundsBySectionNum = new Map<number, AisleSectionBounds>();
  const shelvesBySectionNum = new Map<number, AisleSeamShelf[]>();

  for (const fixture of fixtures) {
    const x = fixture.section_x;
    if (x !== undefined && !boundsBySectionNum.has(fixture.section_num)) {
      boundsBySectionNum.set(fixture.section_num, {
        x,
        width: fixture.section_width,
      });
    }

    const shelves = shelvesBySectionNum.get(fixture.section_num) ?? [];
    shelves.push({
      canCombine: fixture.can_combine,
      isPegboard: isPsaPegboardFixtureType(fixture.type ?? undefined),
    });
    shelvesBySectionNum.set(fixture.section_num, shelves);
  }

  const walkOrder = [...shelvesBySectionNum.keys()].sort(
    (sectionNumA, sectionNumB) => sectionNumA - sectionNumB,
  );

  const bounds = walkOrder.map((sectionNum) => boundsBySectionNum.get(sectionNum) ?? UNPOSITIONED);
  const floorPalletIndices = floorSpans
    .filter(
      (span) =>
        !bounds.some(
          (section) =>
            Math.min(span.x + span.width, section.x + section.width) -
              Math.max(span.x, section.x) >=
            PALLET_UNDER_BAY_SHARE * span.width,
        ),
    )
    .map((span) => bounds.findIndex((section) => section.x >= span.x));

  return suggestAisleSplits(
    bounds,
    walkOrder.map((sectionNum) => ({
      shelves: shelvesBySectionNum.get(sectionNum) ?? [],
    })),
    { minGap: splitGapMinFor(metric), floorSpans },
  ).map(({ bayIndex, ...evidence }): PsaAisleSplitSuggestion => {
    const afterSectionNum = walkOrder[bayIndex];
    const afterBayNo =
      bayIndex + 1 + floorPalletIndices.filter((index) => index >= 0 && index <= bayIndex).length;
    return evidence.evidence === "gap"
      ? {
          afterSectionNum,
          afterBayNo,
          ...evidence,
          gapUnit: metric ? "cm" : "in",
        }
      : { afterSectionNum, afterBayNo, ...evidence };
  });
};
